import { StudyForm } from '../types';

/**
 * Extended StudyForm with tree-related properties
 */
export interface FormTreeNode extends StudyForm {
  children: FormTreeNode[];
  depth: number;
  parent?: FormTreeNode;
}

/**
 * Builds a hierarchical tree structure from flat array of forms based on subFormArray
 * @param forms - Array of StudyForm objects
 * @returns Array of root FormTreeNode objects (forms that are not subforms of anyone)
 */
export function buildFormTree(forms: StudyForm[]): FormTreeNode[] {
  // Create a map of forms by name for quick lookup
  const formsByName = new Map<string, StudyForm>();
  forms.forEach(form => {
    formsByName.set(form.name, form);
  });

  // Create a map of forms by id for quick lookup
  const formsById = new Map<number, StudyForm>();
  forms.forEach(form => {
    formsById.set(form.id, form);
  });

  // Create tree nodes
  const treeNodes = new Map<number, FormTreeNode>();
  forms.forEach(form => {
    treeNodes.set(form.id, {
      ...form,
      children: [],
      depth: 0,
    });
  });

  // Build parent-child relationships based on subFormArray
  forms.forEach(form => {
    if (
      form.json_schema?.subFormArray &&
      Array.isArray(form.json_schema.subFormArray)
    ) {
      const parentNode = treeNodes.get(form.id)!;

      form.json_schema.subFormArray.forEach((subformName: string) => {
        const childForm = formsByName.get(subformName);
        if (childForm) {
          const childNode = treeNodes.get(childForm.id)!;
          // Add child to parent's children list (even if it already has another parent)
          // This allows forms with multiple parents to appear under all their parents
          if (!parentNode.children.some(child => child.id === childNode.id)) {
            parentNode.children.push(childNode);
          }
          // Set parent only if not already set (first parent encountered)
          // This ensures the tree structure is consistent for traversal
          if (!childNode.parent) {
            childNode.parent = parentNode;
          }
        }
      });
    }
  });

  // Calculate depths and find root nodes
  const visited = new Set<number>();
  const rootNodes: FormTreeNode[] = [];

  // DFS to calculate depths and collect root nodes
  function dfs(node: FormTreeNode, depth: number = 0): void {
    if (visited.has(node.id)) return;
    visited.add(node.id);

    node.depth = depth;

    // If node has no parent or parent's subFormArray doesn't include this node, it's a root
    if (!node.parent) {
      rootNodes.push(node);
    }

    // Sort children by name for consistent ordering
    node.children.sort((a, b) => a.name.localeCompare(b.name));

    node.children.forEach(child => dfs(child, depth + 1));
  }

  // Process all nodes to calculate depths and collect roots
  treeNodes.forEach(node => {
    if (!visited.has(node.id)) {
      dfs(node);
    }
  });

  // Sort root nodes by name
  rootNodes.sort((a, b) => a.name.localeCompare(b.name));

  return rootNodes;
}

/**
 * Flattens a form tree back to a flat array, maintaining the hierarchical order
 * @param roots - Array of root FormTreeNode objects
 * @returns Flat array of StudyForm objects in hierarchical order
 */
export function flattenFormTree(roots: FormTreeNode[]): StudyForm[] {
  const result: StudyForm[] = [];

  function dfs(node: FormTreeNode): void {
    // Remove tree-specific properties and add to result
    const { children, depth, parent, ...form } = node;
    void children;
    void depth;
    void parent;
    result.push(form);

    node.children.forEach(child => dfs(child));
  }

  roots.forEach(root => dfs(root));
  return result;
}

/**
 * Finds all descendant forms of a given form (recursive)
 * @param form - The form to find descendants for
 * @param allForms - All available forms
 * @returns Array of descendant form IDs
 */
export function getFormDescendants(
  form: StudyForm,
  allForms: StudyForm[]
): number[] {
  const descendants: number[] = [];
  const tree = buildFormTree(allForms);
  const formMap = new Map<number, FormTreeNode>();

  // Build a flat map for quick lookup
  function buildMap(nodes: FormTreeNode[]): void {
    nodes.forEach(node => {
      formMap.set(node.id, node);
      buildMap(node.children);
    });
  }
  buildMap(tree);

  const node = formMap.get(form.id);
  if (!node) return descendants;

  function collectDescendants(node: FormTreeNode): void {
    node.children.forEach(child => {
      descendants.push(child.id);
      collectDescendants(child);
    });
  }

  collectDescendants(node);
  return descendants;
}

/**
 * Gets the display path for a form (e.g., "Parent > Child > Grandchild")
 * @param form - The form to get path for
 * @param allForms - All available forms
 * @returns String representing the hierarchical path
 */
export function getFormPath(form: StudyForm, allForms: StudyForm[]): string {
  const tree = buildFormTree(allForms);
  const formMap = new Map<number, FormTreeNode>();

  // Build a flat map for quick lookup
  function buildMap(nodes: FormTreeNode[]): void {
    nodes.forEach(node => {
      formMap.set(node.id, node);
      buildMap(node.children);
    });
  }
  buildMap(tree);

  const node = formMap.get(form.id);
  if (!node) return form.name;

  const path: string[] = [];
  let current: FormTreeNode | undefined = node;

  while (current) {
    path.unshift(current.name);
    current = current.parent;
  }

  return path.join(' > ');
}

/**
 * Checks if a form is a descendant of another form
 * @param potentialDescendant - The form that might be a descendant
 * @param potentialAncestor - The form that might be an ancestor
 * @param allForms - All available forms
 * @returns True if potentialDescendant is a descendant of potentialAncestor
 */
export function isDescendantOf(
  potentialDescendant: StudyForm,
  potentialAncestor: StudyForm,
  allForms: StudyForm[]
): boolean {
  const descendants = getFormDescendants(potentialAncestor, allForms);
  return descendants.includes(potentialDescendant.id);
}

/**
 * Builds a nested path JSON string from root to the selected form
 * Format: {"1": {"id": 1, "name": "form1", "5": {"id": 5, "name": "form5"}}}
 * @param formNode - The selected FormTreeNode (must have parent chain)
 * @param allForms - All available forms (for lookup if needed, not currently used)
 * @returns JSON string with nested path structure
 */
export function buildNestedPath(
  formNode: FormTreeNode,
): string {
  // Build path array from root to selected form
  const path: FormTreeNode[] = [];
  let current: FormTreeNode | undefined = formNode;

  // Traverse up to root
  while (current) {
    path.unshift(current);
    current = current.parent;
  }

  // If path is empty (shouldn't happen), return empty object
  if (path.length === 0) {
    return JSON.stringify({});
  }

  // Build nested structure: root form contains child forms as keys
  // Start with root form
  const rootNode = path[0];
  const nestedStructure: Record<string, Record<string, unknown>> = {
    [rootNode.id.toString()]: {
      id: rootNode.id,
      name: rootNode.name,
    },
  };

  // For each subsequent form in the path, nest it under its parent
  let currentLevel = nestedStructure[rootNode.id.toString()] as Record<string, unknown>;
  for (let i = 1; i < path.length; i++) {
    const childNode = path[i];
    const childId = childNode.id.toString();
    
    // Add child as a key in the current level
    currentLevel[childId] = {
      id: childNode.id,
      name: childNode.name,
    };
    
    // Move to the child level for next iteration
    currentLevel = currentLevel[childId] as Record<string, unknown>;
  }

  return JSON.stringify(nestedStructure);
}

/**
 * Builds a nested path JSON string from an array of form IDs (path from root)
 * This version uses the actual path through the tree, not the parent chain
 * Format: {"1": {"id": 1, "name": "form1", "5": {"id": 5, "name": "form5"}}}
 * @param pathFromRoot - Array of form IDs representing the path from root to selected form
 * @param allForms - All available forms for lookup (to get form names)
 * @returns JSON string with nested path structure
 */
export function buildNestedPathFromIds(
  pathFromRoot: number[],
  allForms: StudyForm[]
): string {
  if (pathFromRoot.length === 0) {
    return JSON.stringify({});
  }

  // Create a map for quick lookup
  const formsMap = new Map<number, StudyForm>();
  allForms.forEach(form => {
    formsMap.set(form.id, form);
  });

  // Build nested structure: root form contains child forms as keys
  const rootFormId = pathFromRoot[0];
  const rootForm = formsMap.get(rootFormId);
  
  if (!rootForm) {
    return JSON.stringify({});
  }

  const nestedStructure: Record<string, Record<string, unknown>> = {
    [rootFormId.toString()]: {
      id: rootFormId,
      name: rootForm.name,
    },
  };

  // For each subsequent form in the path, nest it under its parent
  let currentLevel = nestedStructure[rootFormId.toString()] as Record<string, unknown>;
  for (let i = 1; i < pathFromRoot.length; i++) {
    const formId = pathFromRoot[i];
    const form = formsMap.get(formId);
    
    if (!form) {
      // If form not found, skip it
      continue;
    }

    const formIdStr = formId.toString();
    
    // Add child as a key in the current level
    currentLevel[formIdStr] = {
      id: formId,
      name: form.name,
    };
    
    // Move to the child level for next iteration
    currentLevel = currentLevel[formIdStr] as Record<string, unknown>;
  }

  return JSON.stringify(nestedStructure);
}

/**
 * Checks if the path from root to the given form node matches the nested_path
 * @param pathFromRoot - Array of FormTreeNode IDs representing the path from root to current node
 * @param nestedPathJson - JSON string with nested path structure
 * @returns True if the path matches nestedPathJson
 */
export function matchesNestedPath(
  pathFromRoot: number[],
  nestedPathJson?: string
): boolean {
  if (!nestedPathJson) {
    // If no nested_path, fall back to simple ID check (for backward compatibility)
    return false;
  }

  try {
    // Parse the nested path JSON
    const savedPath = JSON.parse(nestedPathJson);

    if (pathFromRoot.length === 0) {
      return false;
    }

    // Check if the path matches the saved nested_path structure
    let currentLevel = savedPath;
    for (let i = 0; i < pathFromRoot.length; i++) {
      const nodeId = pathFromRoot[i].toString();

      // Check if current level has this node ID as a key
      if (!currentLevel || typeof currentLevel !== 'object' || !(nodeId in currentLevel)) {
        return false;
      }

      // Move to the next level if not the last node
      if (i < pathFromRoot.length - 1) {
        currentLevel = currentLevel[nodeId];
      }
    }

    return true;
  } catch (error) {
    // If parsing fails, return false
    console.warn('Failed to parse nested_path:', error);
    return false;
  }
}

/**
 * Extracts the path (array of form IDs) from a nested_path JSON string
 * @param nestedPathJson - JSON string with nested path structure
 * @returns Array of form IDs from root to selected form, or empty array if invalid
 */
export function extractPathFromNestedPath(
  nestedPathJson?: string
): number[] {
  if (!nestedPathJson) {
    return [];
  }

  try {
    const savedPath = JSON.parse(nestedPathJson);
    const path: number[] = [];

    function traverse(obj: unknown): void {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      const record = obj as Record<string, unknown>;

      // Get all keys that are form IDs (numeric strings)
      const keys = Object.keys(record).filter(key => {
        const numKey = parseInt(key);
        const value = record[key];
        return (
          !isNaN(numKey) &&
          value &&
          typeof value === 'object' &&
          'id' in (value as Record<string, unknown>)
        );
      });

      if (keys.length === 0) {
        return;
      }

      // In nested_path structure, each level should have one form ID key
      // We take the first one and traverse deeper
      const key = keys[0];
      const formId = parseInt(key);
      path.push(formId);
      
      // Recursively traverse the nested structure
      traverse(record[key]);
    }

    traverse(savedPath);
    return path;
  } catch (error) {
    console.warn('Failed to parse nested_path:', error);
    return [];
  }
}

/**
 * Gets the parent form ID from a nested_path JSON string
 * @param nestedPathJson - JSON string with nested path structure
 * @param formId - The ID of the form to get parent for
 * @returns Parent form ID, or null if not found or form is root
 */
export function getParentFormIdFromNestedPath(
  nestedPathJson?: string,
  formId?: number
): number | null {
  if (!nestedPathJson || formId === undefined) {
    return null;
  }

  const path = extractPathFromNestedPath(nestedPathJson);
  const formIndex = path.indexOf(formId);
  
  // If form is not in path or is root (index 0), return null
  if (formIndex <= 0) {
    return null;
  }

  // Return the parent (previous element in path)
  return path[formIndex - 1];
}
